import React, { Component } from 'react';
import { styled } from '@compiled/react';

import Heading from '@atlaskit/heading';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { AdControlProvider } from '@atlassian/ad-control-toolkit';
import { SOFTWARE_PROJECT } from '@atlassian/jira-common-constants/src/project-types';
import { LICENSED_PRODUCTS } from '@atlassian/jira-common-util-get-tenant-context';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { ff } from '@atlassian/jira-feature-flagging';
import { componentWithFF } from '@atlassian/jira-feature-flagging-utils';
import { FormattedMessage } from '@atlassian/jira-intl';
import { getLocalStorageData } from '@atlassian/jira-issue-create-confluence-content/src/controllers/utils/get-local-storage';
import {
	ConfluencePageLineCardGroup,
	ConfluenceWhiteboardLineCardGroup,
} from '@atlassian/jira-issue-view-common-views/src/confluence-content-line-card';
import LineCardSkeleton from '@atlassian/jira-issue-view-common-views/src/skeleton/item-line-card-group';
import { Container } from '@atlassian/jira-issue-view-common-views/src/smart-link-content/styled';
import {
	SectionHeading,
	SectionHeadingTitle,
	SectionHeadingIcons,
} from '@atlassian/jira-issue-view-common/src/component/section-heading/section-heading-view';
import {
	isCrossJoinContentPreviewEnabled,
	isFitAndFinishFullPageMilestone2,
	isWhiteboardStandaloneEnabled,
} from '@atlassian/jira-issue-view-feature-flags';
import Placeholder from '@atlassian/jira-placeholder';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import { isConfluencePageDummyLinkExperiment } from '../../feature-flags';
import { ELIGIBLE_ISSUE_TYPES } from '../confluence-placeholder-template-experiment/common/constants';
import { ConfluenceDummyLinkProvider } from '../confluence-placeholder-template-experiment/controllers/confluence-dummy-link-context';
import AddButton from './add-button';
import ConfluencePageCreateLinkView from './create-link';
import DummyConfluenceLinkViewLazy from './dummy-confluence-link-view-lazy';
import { LinkedMentionedContentPreview } from './linked-mentioned-content-preview';
import type { ConfluenceContentProps } from './types';

const LINKED_PAGES_GROUP_ID = 'linked-confluence-pages';
const LINKED_WHITEBOARDS_GROUP_ID = 'linked-confluence-whiteboards';
const MENTIONED_PAGES_GROUP_ID = 'mentioned-confluence-pages';

type RenderConfluenceContentFragmentProps = {
	totalConfluenceContentCount: number;
	showContentPreview: boolean;
	eligibleForLinkedPageCrossJoinExperiment: boolean;
};

// eslint-disable-next-line jira/react/no-class-components
export class ConfluenceContentView extends Component<ConfluenceContentProps> {
	static defaultProps = {
		isConfluencePageLinksEnabled: false,
		linkedPagesCachedCount: 0,
		linkedWhiteboardsCachedCount: 0,
		mentionedPagesCachedCount: 0,
	};

	isIssueTypeEligibleForPlaceholderTemplate() {
		return this.props.issueType
			? ELIGIBLE_ISSUE_TYPES.includes(this.props.issueType.name.toLowerCase())
			: false;
	}

	shouldShowConfluenceContent() {
		return (
			this.props.isCreateLinkedPageOpened ||
			this.hasLinkedPages() ||
			this.hasMentionedPages() ||
			(isWhiteboardStandaloneEnabled() && this.hasLinkedWhiteboards())
		);
	}

	getConfluenceContentPreviewDetails() {
		const { permitted } = getLocalStorageData('userConfluenceAccess');
		const tenantHasConfluence = !!this.props.licensedProducts?.[LICENSED_PRODUCTS.CONFLUENCE];
		const totalConfluenceContentCount = this.getTotalContentCount();
		const canCrossJoin = tenantHasConfluence && !permitted && totalConfluenceContentCount > 0;
		const eligibleForLinkedPageCrossJoinExperiment =
			isCrossJoinContentPreviewEnabled() && canCrossJoin;

		const [embedConfluenceConfig] = UNSAFE_noExposureExp(
			'platform_embed-conf_jira-issue_cross-join_exp',
		);
		const embedConfluenceCohort = embedConfluenceConfig.get('cohort', 'not-enrolled');
		const showContentPreview =
			eligibleForLinkedPageCrossJoinExperiment &&
			ff('platform_embed-conf_jira-issue_create_cross-join') &&
			embedConfluenceCohort === 'experiment'; // enrolled in the test/experiment cohort

		return {
			showContentPreview,
			eligibleForLinkedPageCrossJoinExperiment,
			totalConfluenceContentCount,
		};
	}

	/*
	 *  The linked page count is a cached value that
	 *  may be out of date, so use the actual fetched
	 *  pages' count once this is available.
	 */
	hasLinkedPages() {
		return this.props.linkedPages && this.props.linkedPages.length > 0;
	}

	/*
	 *  The linked whiteboard count is a cached value that
	 *  may be out of date, so use the actual fetched
	 *  pages' count once this is available.
	 */
	hasLinkedWhiteboards = () =>
		this.props.linkedWhiteboards && this.props.linkedWhiteboards.length > 0;

	/*
	 *  The mentioned page count is a cached value that
	 *  may be out of date, so use the actual fetched
	 *  pages' count once this is available.
	 */
	hasMentionedPages() {
		return this.props.mentionedPages && this.props.mentionedPages.length > 0;
	}

	hasAnyLinkedPageLoaded() {
		return this.props.linkedPages && this.props.linkedPages.length > 0;
	}

	hasAnyMentionedPageLoaded() {
		return this.props.mentionedPages && this.props.mentionedPages.length > 0;
	}

	renderHeading = () => {
		const { canLinkConfluencePage, onAddButtonClicked } = this.props;

		return (
			<StyledSectionHeading>
				<SectionHeadingTitle>
					{isWhiteboardStandaloneEnabled() ? (
						<FormattedMessage
							id="issue.details.confluence-content.heading.non-final"
							defaultMessage="Confluence content"
							description="Heading for linked Confluence content section."
						/>
					) : (
						<FormattedMessage
							id="issue.details.confluence-pages.heading"
							defaultMessage="Confluence pages"
							description="Heading for linked Confluence pages section."
						/>
					)}
				</SectionHeadingTitle>
				<SectionHeadingIcons>
					{canLinkConfluencePage && this.hasLinkedPages() && (
						<AddButton onClick={onAddButtonClicked} />
					)}
				</SectionHeadingIcons>
			</StyledSectionHeading>
		);
	};

	renderLinkedPagesIfAny = () =>
		this.hasAnyLinkedPageLoaded() && this.props.linkedPages ? (
			<ConfluencePageLineCardGroup
				groupId={LINKED_PAGES_GROUP_ID}
				pages={this.props.linkedPages}
				onClick={this.props.onLinkedPageClicked}
				onAuthenticateApplink={this.props.onAuthenticateApplink}
				onDeleteConfluencePageLink={this.props.onDeleteConfluencePageLink}
				systemConfluenceAppLinkUrl={this.props.systemConfluenceAppLinkUrl}
			/>
		) : null;

	renderLinkedWhiteboardsIfAny = () =>
		this.hasLinkedWhiteboards() ? (
			<ConfluenceWhiteboardLineCardGroup
				groupId={LINKED_WHITEBOARDS_GROUP_ID}
				whiteboards={this.props.linkedWhiteboards || []}
				onClick={this.props.onLinkedWhiteboardClicked}
				systemConfluenceAppLinkUrl={this.props.systemConfluenceAppLinkUrl}
			/>
		) : null;

	renderMentionedPagesIfAny = () =>
		this.hasAnyMentionedPageLoaded() && this.props.mentionedPages ? (
			<ConfluencePageLineCardGroup
				groupId={MENTIONED_PAGES_GROUP_ID}
				pages={this.props.mentionedPages}
				onClick={this.props.onMentionedPageClicked}
				onAuthenticateApplink={this.props.onAuthenticateApplink}
				onDeleteConfluencePageLink={this.props.onDeleteConfluencePageLink}
				systemConfluenceAppLinkUrl={this.props.systemConfluenceAppLinkUrl}
			/>
		) : null;

	renderLinkedPagesAndWhiteboards() {
		return (
			<GroupContainer>
				{(this.props.linkedPages || this.props.linkedWhiteboards) && (
					<Container>
						{this.renderLinkedPagesIfAny()}
						{this.renderLinkedWhiteboardsIfAny()}
					</Container>
				)}
				{!this.props.linkedPages && (
					<LineCardSkeleton itemCount={this.props.linkedPagesCachedCount ?? 0} />
				)}
				{!this.props.linkedWhiteboards && (
					<LineCardSkeleton itemCount={this.props.linkedWhiteboardsCachedCount ?? 0} />
				)}
			</GroupContainer>
		);
	}

	renderLinkedPages() {
		return (
			<GroupContainer>
				{this.props.linkedPages ? (
					<Container>{this.renderLinkedPagesIfAny()}</Container>
				) : (
					<LineCardSkeleton itemCount={this.props.linkedPagesCachedCount ?? 0} />
				)}
			</GroupContainer>
		);
	}

	renderMentionedPage() {
		return isFitAndFinishFullPageMilestone2() ? (
			<Box xcss={containerWrapperStyles}>
				<Container>{this.renderMentionedPagesIfAny()}</Container>
			</Box>
		) : (
			<Container>{this.renderMentionedPagesIfAny()}</Container>
		);
	}

	renderMentionedPages() {
		return (
			<GroupContainer>
				<Heading as="h3" level="h200">
					<FormattedMessage
						id="issue.details.confluence-pages.mentioned-on"
						defaultMessage="mentioned on"
					/>
				</Heading>
				{this.props.mentionedPages ? (
					this.renderMentionedPage()
				) : (
					<LineCardSkeleton itemCount={this.props.mentionedPagesCachedCount ?? 0} />
				)}
			</GroupContainer>
		);
	}

	renderCreateLinkedPage() {
		return this.props.isCreateLinkedPageOpened ? <ConfluencePageCreateLinkView /> : null;
	}

	renderConfluenceContentFragment({
		totalConfluenceContentCount,
		showContentPreview,
		eligibleForLinkedPageCrossJoinExperiment,
	}: RenderConfluenceContentFragmentProps) {
		const shouldDisplayHeading = this.shouldShowConfluenceContent() || showContentPreview;
		return (
			<>
				{shouldDisplayHeading && this.renderHeading()}
				{!isWhiteboardStandaloneEnabled() && this.hasLinkedPages() && this.renderLinkedPages()}
				{isWhiteboardStandaloneEnabled() &&
					(this.hasLinkedPages() || this.hasLinkedWhiteboards()) &&
					this.renderLinkedPagesAndWhiteboards()}
				{this.hasMentionedPages() && this.renderMentionedPages()}
				{eligibleForLinkedPageCrossJoinExperiment && (
					<LinkedMentionedContentPreview contentCount={totalConfluenceContentCount} />
				)}
				{this.renderCreateLinkedPage()}
			</>
		);
	}

	renderConfluenceDummyLink() {
		return (
			<ContextualAnalyticsData
				sourceName="confluencePlaceholderTemplate"
				sourceType={SCREEN}
				attributes={{ experiment: 'placeholderTemplateContextual' }}
			>
				<Placeholder name="confluence-dummy-link-experiment" fallback={null}>
					<ConfluenceDummyLinkProvider>
						<AdControlProvider>
							<DummyConfluenceLinkViewLazy
								issueType={this.props.issueType}
								tenantHasConfluenceAppLinks={this.props.tenantHasConfluenceAppLinks}
								projectName={this.props.projectName}
							/>
						</AdControlProvider>
					</ConfluenceDummyLinkProvider>
				</Placeholder>
			</ContextualAnalyticsData>
		);
	}

	getTotalContentCount() {
		/* In order to display a preview for linked & mentioned confluence content,
            we need to exclude the count of app linked content if any. When the user dosn't have
            access to confluence in the current site, but has access to an applinked site, then
            we still want to show the applinked content to the user. The links to be displayed are contained with the
            'pages' or the 'whiteboards' array selector. The length of these arrays should be subtracted from the cached count,
            because cached count would have the total number of all links, including the applinked ones.

            For a scenario where these arrays are empty, we display the cahced count as is. (Unless there are
            any config errors with the applinks, the array must have links to the app-linked content).
        */
		const {
			linkedPagesCachedCount = 0,
			mentionedPagesCachedCount = 0,
			linkedWhiteboardsCachedCount = 0,
			linkedPages,
			linkedWhiteboards,
			mentionedPages,
		} = this.props;
		const linkedPagesCount = linkedPages?.length ?? 0;
		const mentionedPagesCount = mentionedPages?.length ?? 0;
		const linkedWhiteboardsCount = linkedWhiteboards?.length ?? 0;

		const totalCount =
			linkedPagesCachedCount -
			linkedPagesCount +
			(mentionedPagesCachedCount - mentionedPagesCount) +
			(linkedWhiteboardsCachedCount - linkedWhiteboardsCount);

		return totalCount < 0 ? 0 : totalCount;
	}

	render() {
		const {
			showContentPreview,
			totalConfluenceContentCount,
			eligibleForLinkedPageCrossJoinExperiment,
		} = this.getConfluenceContentPreviewDetails();
		if (
			this.props.isConfluencePageLinksEnabled &&
			(this.shouldShowConfluenceContent() || eligibleForLinkedPageCrossJoinExperiment)
		) {
			return this.renderConfluenceContentFragment({
				totalConfluenceContentCount,
				showContentPreview,
				eligibleForLinkedPageCrossJoinExperiment,
			});
		}

		if (
			isConfluencePageDummyLinkExperiment() &&
			this.props?.projectType === SOFTWARE_PROJECT &&
			this.isIssueTypeEligibleForPlaceholderTemplate()
		) {
			return this.renderConfluenceDummyLink();
		}

		return null;
	}
}

// 1px padding to the right to be aligned with the add comment box because of the shadow around it
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const GroupContainer = styled.div({
	margin: `${token('space.100', '8px')} 0`,
	paddingRight: token('space.025', '2px'),
});

const StyledSectionHeading = componentWithFF(
	'issue-view-fit-and-finish-full-page-milestone2_ebk0i',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
	styled(SectionHeading)<{}>({
		paddingBottom: token('space.100', '8px'),
	}),
	SectionHeading,
);

const containerWrapperStyles = xcss({
	marginTop: token('space.100', '8px'),
});
